import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { useAssessment, useLinks, useLogFrontendEvents, useMissionRoleApplicant, useParamFunctions } from "hooks";
import Modal, { MODAL_SIZE } from "components/Modal";
import { MessagesError } from "components/Messages";
import { ProfileAiSummary, ProfileBio, ProfileVideoInterviewsList } from "components/Profile";
import { Col, Grid } from "components/Containers";
import {
  ApplicantMatchScore,
  ApplicationChallenges,
  ApplicationMatchingTags,
  ApplicationNotesForm,
  ApplicationQuestionnaireResponses,
  ApplicationRate,
  ApplicantNegotiatedRate,
  ApplicationPreviousAssignments,
  ApplicationUpdateSelect,
  ApplicantListLayout,
  ApplicationNotSelectedReasons,
  ApplicantPdfDownloadLink,
  ApplicantMarginCalculator,
  ApplicationHighlights,
  ApplicantPreviewLink,
  ApplicationRequirements,
  ApplicationProgressBanner,
} from "components/Missions/Applicants";
import { COMMENTABLE_TYPES } from "constants";
import Comments from "components/Comments";
import Icon, { ICON_SIZE, ICON_TYPE, withIcon } from "components/Icon";
import { SectionTitle } from "components/Layouts";
import Card from "components/Card";
import { PrimaryLink } from "components/Links";
import { RoleLink } from "components/Missions/Roles";
import { H2 } from "components/Text";
import { formatDate } from "utils";
import { breakpoints, colors, fontSize } from "style";
import { SIZE } from "components/Missions/Applicants/ApplicantMatchScore";
import { OrganizationLink } from "components/Organization";
import { MissionLink } from "components/Missions";
import ApplicantInterviewInviteButton from "./ApplicantInterviewInviteButton";
import {
  APPLICANT_LIST_COLUMNS,
  APPLICANT_TYPES,
  FRONTEND_EVENT_KEYS,
  FRONTEND_EVENTABLE_TYPES,
  getApplicationQuestionnaireItems,
  MISSION_ROLE_STATES,
} from "constants/index";

const DEFAULT_EXPANDABLE_STATE = {
  matchingTags: false,
  rateMatchingTags: false,
  challengeResponses: false,
  questionnaireResponses: false,
  notes: false,
  clientNotes: false,
  otherApplications: false,
  videoInterviews: false,
  previousTeams: false,
  aiSummary: true,
  highlightTags: true,
  acceptedRequirements: false,
};

/**
 * ApplicationDetailsModal
 *
 * @param {Object}    application
 * @param {Function}  setApplication
 * @param {Boolean}   show
 */
const ApplicationDetailsModal = ({ application, setApplication, show: isOpen, ...props }) => {
  if (!application) return null;

  const { removeParam } = useParamFunctions();
  const [challenges, setChallenges] = useState([]);
  const { loading, data, error, list, updateApplicant } = useMissionRoleApplicant({ id: application.id });
  const links = useLinks();
  const isRoleClosed = data?.missionRole?.state === MISSION_ROLE_STATES.closed;
  const [show, setShow] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [averageScore, setAverageScore] = useState(0);
  const [marginSellingData, setMarginSellingData] = useState(null);

  const { data: assessment, loading: assessmentLoading } = useAssessment({
    id: data?.missionRole?.assessmentId,
    resourceId: data?.profile?.id,
  });

  // Track applicant viewed event for analytics
  useLogFrontendEvents({
    eventableId: data?.id,
    eventableType: FRONTEND_EVENTABLE_TYPES.missionRoleApplicant,
    key: FRONTEND_EVENT_KEYS.missionRoleApplicantViewed,
  });

  const questionCount = getApplicationQuestionnaireItems(data, assessment?.questions).length;

  const [expandable, setExpandable] = useState(DEFAULT_EXPANDABLE_STATE);

  useEffect(() => {
    if (data) {
      setChallenges(data.profile.challengeResponses);
    }
  }, [data]);

  // Reset expandable state when a new application is set
  useEffect(() => {
    if (application) {
      setExpandable(DEFAULT_EXPANDABLE_STATE);
    }
  }, [application]);

  const handleExpand = (isExpanded, expandableId) => setExpandable((prev) => ({ ...prev, [expandableId]: isExpanded }));

  useEffect(() => {
    setAverageScore(
      calculateAverage(data?.challengesCompletedByProfile?.length ? data?.challengesCompletedByProfile : challenges)
    );
  }, [data?.challengesCompletedByProfile, challenges]);

  const handleUpdateApplicant = (params) => {
    updateApplicant(data?.id, { negotiatedRate: params });
  };

  const handleUpdateSellingRate = (params) => {
    updateApplicant(data?.id, { negotiatedSellingRate: params });
    setMarginSellingData(null);
  };

  const handleModalClose = () => {
    setApplication(null);
    removeParam("application");
  };

  return (
    <>
      <Modal
        title={
          <div css={styles.modal_title}>
            <FormattedMessage
              id="Missions.MissionRole.ApplicationDetailsModal.Title"
              values={{
                id: <span css={styles.applicant_id}>{data?.missionRole?.slug}</span>,
              }}
            />
            {data && (
              <ApplicationUpdateSelect
                missionRoleApplicant={data}
                loading={loading}
                css={{
                  width: "15rem",
                }}
              />
            )}
          </div>
        }
        info={
          <span css={styles.modal_info}>
            {data && <ApplicantPreviewLink applicant={data} />}
            <span css={styles.separator} />
            {data && <ApplicantPdfDownloadLink applicant={data} />}
            <span css={styles.separator} />
            <PrimaryLink onClick={() => setShow((prev) => !prev)} disabled={show}>
              <FormattedMessage
                id="Missions.MissionRole.ApplicationDetailsModal.ShowComments"
                values={{
                  count: commentCount,
                }}
              />
              <Icon type={ICON_TYPE.arrowRight} color="inherit" />
            </PrimaryLink>
          </span>
        }
        loading={loading}
        secondaryButtonMessageId={isRoleClosed ? "Global.Close" : "Global.SaveClose"}
        showSave={false}
        show={isOpen}
        size={MODAL_SIZE.large}
        onClose={handleModalClose}
        key={data?.id}
        {...props}
      >
        {error && <MessagesError />}
        <Grid gap="1rem">
          <Grid.col start={1} end={13} css={styles.application_header}>
            <Col gap="0.5rem" css={styles.application_heading}>
              <H2 css={styles.title} loading={loading}>
                <FormattedMessage
                  id="Missions.MissionRole.ApplicationDetailsModal.Header"
                  values={{
                    role: <RoleLink mission={data?.missionRole?.mission} missionRole={data?.missionRole} withOverlay />,
                    mission: <MissionLink data={data?.missionRole?.mission} withOverlay />,
                    org: <OrganizationLink data={data?.mission?.organization} withOverlay />,
                  }}
                />
              </H2>
              <span css={styles.subtitle}>
                <FormattedMessage
                  id="Missions.MissionRole.ApplicationDetailsModal.AppliedOn"
                  values={{
                    date: data?.appliedAt ? formatDate(data.appliedAt, "MMMM Do, YYYY", false) : "—",
                  }}
                />
              </span>
            </Col>
            <div css={styles.application_actions}>
              {data?.applicationChecklist?.length > 0 && (
                <ApplicationProgressBanner missionRoleApplicant={data} css={styles.progress_banner} />
              )}
              {data?.id === application.id && (
                <ApplicantInterviewInviteButton
                  application={data}
                  isParentOpen={isOpen}
                  disabled={loading}
                  css={styles.schedule_button}
                />
              )}
            </div>
          </Grid.col>
          <Grid.col start={1} end={13}>
            <Card>
              <ProfileBio
                loading={loading}
                data={data?.profile}
                isEditable={false}
                hideDescription={true}
                profilePicProps={{
                  width: `12rem`,
                  height: `12rem`,
                }}
              />
              <LinkProfile href={links.private_profile(data?.profile?.slug)} withMoreIcon routerEnabled>
                <FormattedMessage id="Profile.ProfileBio.ViewPrivateLink" />
              </LinkProfile>
            </Card>
          </Grid.col>
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={<FormattedMessage id="Missions.Applicants.ApplicationDetailsModal.TitleAISummary" />}
              expandableId="aiSummary"
              isExpanded={expandable.aiSummary}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.aiSummary === true && (
            <Grid.col start={1} end={13}>
              <Card>
                <ProfileAiSummary profileId={data?.profile?.uuid} loading={loading} />
              </Card>
            </Grid.col>
          )}
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={<FormattedMessage id="Missions.Applicants.ApplicationDetailsModal.TitleHighlightTags" />}
              expandableId="highlightTags"
              isExpanded={expandable.highlightTags}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.highlightTags === true && (
            <Grid.col start={1} end={13}>
              <Card>
                <ApplicationHighlights loading={loading || list.loading} data={data} />
              </Card>
            </Grid.col>
          )}
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={
                <FormattedMessage
                  id="Missions.Applicants.ApplicationDetailsModal.TitleMatchingTags"
                  values={{
                    count: (
                      <ApplicantMatchScore
                        loading={loading}
                        score={data?.matchScore * 100}
                        size={SIZE.small}
                        isNull={!data?.matchScore && data?.matchScore !== 0}
                        isPercent
                      />
                    ),
                  }}
                />
              }
              expandableId="matchingTags"
              isExpanded={expandable.matchingTags}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.matchingTags === true && (
            <Grid.col start={1} end={13}>
              <Card>
                <ApplicationMatchingTags loading={loading} data={data} />
              </Card>
            </Grid.col>
          )}
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={
                <FormattedMessage
                  id="Missions.Applicants.ApplicationDetailsModal.TitleRateMatchingTags"
                  values={{
                    count: (
                      <ApplicantMatchScore
                        loading={loading}
                        score={data?.rateScore * 100}
                        size={SIZE.small}
                        isNull={!data?.rateScore && data?.rateScore !== 0}
                        isPercent
                      />
                    ),
                  }}
                />
              }
              expandableId="rateMatchingTags"
              isExpanded={expandable.rateMatchingTags}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.rateMatchingTags === true && (
            <>
              <Grid.col start={1} end={13}>
                <Card>
                  <ApplicationRate loading={loading} data={data} />
                </Card>
              </Grid.col>
              <Grid.col start={1} end={13}>
                <ApplicantNegotiatedRate
                  rate={data?.negotiatedRate}
                  loading={loading}
                  messageFragment="ApplicantNegotiatedBuyingRate"
                  onSave={handleUpdateApplicant}
                  onRemove={() => handleUpdateApplicant({ amount: null, currency: null, frequency: null })}
                  canEdit={!isRoleClosed}
                />
              </Grid.col>
              <Grid.col start={1} end={13}>
                <ApplicantMarginCalculator
                  loading={loading}
                  data={data}
                  onValueSelect={(sellingData) => setMarginSellingData(sellingData)}
                />
              </Grid.col>
              <Grid.col start={1} end={13}>
                <ApplicantNegotiatedRate
                  rate={data?.negotiatedSellingRate}
                  preFilledData={marginSellingData}
                  loading={loading}
                  messageFragment="ApplicantNegotiatedSellingRate"
                  onSave={handleUpdateSellingRate}
                  onRemove={() => handleUpdateSellingRate({ amount: null, currency: null, frequency: null })}
                  buyingAmount={data?.negotiatedRate?.amount}
                  canEdit={!isRoleClosed}
                  onCancel={() => setMarginSellingData(null)}
                />
              </Grid.col>
            </>
          )}
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={
                <FormattedMessage
                  id="Missions.Applicants.ApplicantDetailsModal.LabelVideoInterviews"
                  values={{
                    count: (
                      <ApplicantMatchScore
                        loading={loading || list?.loading}
                        score={data?.profile?.completedInterviewsCount}
                        size={SIZE.small}
                      />
                    ),
                  }}
                />
              }
              expandableId="videoInterviews"
              isExpanded={expandable.videoInterviews}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.videoInterviews === true && (
            <Grid.col start={1} end={13}>
              <Card>
                <ProfileVideoInterviewsList profileId={data?.profile?.id} missionRoleId={data?.missionRole?.id} />
              </Card>
            </Grid.col>
          )}
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={
                <FormattedMessage
                  id="Missions.Applicants.ApplicationDetailsModal.TitleChallenges"
                  values={{
                    count: (
                      <ApplicantMatchScore
                        loading={loading}
                        score={averageScore}
                        size={SIZE.small}
                        isPercent={true}
                        isNull={challenges?.length === 0}
                      />
                    ),
                  }}
                />
              }
              expandableId="challengeResponses"
              isExpanded={expandable.challengeResponses}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.challengeResponses === true && (
            <Grid.col start={1} end={13}>
              <Card>
                <ApplicationChallenges
                  loading={loading}
                  challenges={challenges}
                  missionRoleChallenges={data?.challengesCompletedByProfile}
                />
              </Card>
            </Grid.col>
          )}
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={
                <FormattedMessage
                  id="Missions.Applicants.ApplicationDetailsModal.TitleQuestionnaire"
                  values={{
                    count: <ApplicantMatchScore loading={loading} score={questionCount} size={SIZE.small} />,
                  }}
                />
              }
              expandableId="questionnaireResponses"
              isExpanded={expandable.questionnaireResponses}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.questionnaireResponses === true && (
            <Grid.col start={1} end={13}>
              <Card>
                <ApplicationQuestionnaireResponses
                  loading={loading || assessmentLoading}
                  data={data}
                  assessment={assessment}
                />
              </Card>
            </Grid.col>
          )}
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={
                <FormattedMessage
                  id="Missions.Applicants.ApplicantDetailsModal.LabelOtherApplications"
                  values={{
                    count: (
                      <ApplicantMatchScore
                        loading={loading || list?.loading}
                        score={list?.data?.missionRoleApplicants?.totalCount}
                        size={SIZE.small}
                      />
                    ),
                  }}
                />
              }
              expandableId="otherApplications"
              isExpanded={expandable.otherApplications}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.otherApplications === true && (
            <Grid.col start={1} end={13}>
              <ApplicantListLayout
                showAdvancedOptions={false}
                initialProgressMenu={0}
                hideFilters={true}
                hideSorts={true}
                hideTabs={true}
                hidePages={true}
                hideProgressMenu={true}
                filtersProp={{
                  profileId: data?.profile?.id,
                  excludingMissionRoleId: data?.missionRole?.id,
                }}
                variablesProp={{ includeScores: false }}
                columns={[
                  APPLICANT_LIST_COLUMNS.mission,
                  APPLICANT_LIST_COLUMNS.state,
                  APPLICANT_LIST_COLUMNS.created,
                  APPLICANT_LIST_COLUMNS.applied,
                  APPLICANT_LIST_COLUMNS.applicationRate,
                  APPLICANT_LIST_COLUMNS.viewButton,
                ]}
                disablePagination
              />
            </Grid.col>
          )}
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={
                <FormattedMessage
                  id="Missions.Applicants.ApplicantDetailsModal.LabelPreviousTeams"
                  values={{
                    count: (
                      <ApplicantMatchScore
                        loading={loading}
                        score={data?.profile?.allTimeAssignmentsCount}
                        size={SIZE.small}
                      />
                    ),
                  }}
                />
              }
              expandableId="previousTeams"
              isExpanded={expandable.previousTeams}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.previousTeams === true && (
            <Grid.col start={1} end={13}>
              <Card>
                <ApplicationPreviousAssignments profileId={data?.profile?.id} />
              </Card>
            </Grid.col>
          )}
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={
                <FormattedMessage
                  id="Missions.Applicants.ApplicantDetailsModal.LabelRequirements"
                  values={{
                    count: (
                      <ApplicantMatchScore
                        loading={loading}
                        score={data?.acceptedRequirements?.length}
                        size={SIZE.small}
                      />
                    ),
                  }}
                />
              }
              expandableId="acceptedRequirements"
              isExpanded={expandable.acceptedRequirements}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.acceptedRequirements === true && (
            <Grid.col start={1} end={13}>
              <ApplicationRequirements application={data} showMissingRequirements />
            </Grid.col>
          )}
          {data?.state === APPLICANT_TYPES.unselected && (
            <>
              <Grid.col start={1} end={13}>
                <SectionTitle
                  title={
                    <FormattedMessage
                      id="Missions.Applicants.ApplicantDetailsModal.LabelNotSelectedReasons"
                      values={{
                        count: (
                          <ApplicantMatchScore
                            loading={loading}
                            score={data.notSelectedReasons.length}
                            size={SIZE.small}
                          />
                        ),
                      }}
                    />
                  }
                  expandableId="notSelectedReasons"
                  isExpanded={expandable.notSelectedReasons}
                  onExpand={handleExpand}
                  isExpandable
                />
              </Grid.col>
              {expandable.notSelectedReasons === true && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <ApplicationNotSelectedReasons application={data} loading={loading} />
                  </Card>
                </Grid.col>
              )}
            </>
          )}
          <Grid.col start={1} end={13}>
            <hr />
          </Grid.col>
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={<FormattedMessage id="Missions.Applicants.ApplicantDetailsModal.LabelTalentNotes" />}
              expandableId="notes"
              isExpanded={expandable.notes}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.notes === true && (
            <Grid.col start={1} end={13}>
              <ApplicationNotesForm
                summaryFieldName="aiNoteSummary"
                loading={loading}
                fieldName="privateNotes"
                data={data}
                readOnly={isRoleClosed}
              />
            </Grid.col>
          )}
          <Grid.col start={1} end={13}>
            <hr />
          </Grid.col>
          <Grid.col start={1} end={13}>
            <SectionTitle
              title={<FormattedMessage id="Missions.Applicants.ApplicantDetailsModal.LabelClientNotes" />}
              expandableId="clientNotes"
              isExpanded={expandable.clientNotes}
              onExpand={handleExpand}
              isExpandable
            />
          </Grid.col>
          {expandable.clientNotes === true && (
            <Grid.col start={1} end={13}>
              <ApplicationNotesForm loading={loading} fieldName="clientNotes" data={data} readOnly={isRoleClosed} />
            </Grid.col>
          )}
        </Grid>
      </Modal>
      <Comments
        commentableId={data?.id}
        commentableType={COMMENTABLE_TYPES.missionRoleApplicant}
        loading={loading}
        show={show}
        onShow={setShow}
        onLoaded={setCommentCount}
      />
    </>
  );
};

const calculateAverage = (challenges) => {
  const sumScores = challenges.reduce((accumulator, current) => {
    const score = current.score || 0;
    const maxScore = current.maxScore || 1; // Avoid division by zero
    return accumulator + score / maxScore;
  }, 0);

  const averageScore = (sumScores / challenges.length) * 100;
  return Number(averageScore.toFixed(2));
};

const LinkProfile = withIcon(PrimaryLink, {
  type: ICON_TYPE.eye,
  size: ICON_SIZE.medium,
  color: "inherit",
});

const styles = {
  applicant_id: css`
    color: ${colors.grayAnatomyLight1};
    font-weight: 300;
  `,
  modal_title: css`
    display: flex;
    align-items: center;
    gap: 1rem;
  `,
  application_header: css`
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem 2rem;
  `,
  application_heading: css`
    flex: 1 1 30rem;
    min-width: 0;
  `,
  application_actions: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    width: 100%;

    @media (max-width: ${breakpoints.mobile}) {
      width: 100%;

      > * {
        flex: 1 1 auto;
      }
    }
  `,
  progress_banner: css`
    margin-left: 0;
  `,
  schedule_button: css`
    margin-left: auto;
  `,
  title: css`
    font-size: ${fontSize.xlarge};
    font-weight: 400;
  `,
  header_container: css`
    width: 100%;
    justify-content: center;
    align-items: center;
  `,
  subtitle: css`
    color: ${colors.grayAnatomyLight1};
    font-size: ${fontSize.medium};
  `,
  separator: css`
    height: 1em;
    border-left: 1px solid #ccc;
  `,
  modal_info: css`
    display: inline-flex;
    alignitems: center;
    gap: 1rem;
  `,
};

ApplicationDetailsModal.propTypes = {
  application: PropTypes.object.isRequired,
  setApplication: PropTypes.func.isRequired,
  show: PropTypes.bool,
};

export default ApplicationDetailsModal;
